import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { WellsQueryResponse } from 'types/api-responses';

/**
 * Looking a well up by name or UWI, for the picker's direct search. Posts to
 * `queries/wells`, the query behind the Map View's search. Through React Query, not
 * `searchAtomPair`: that has its own Jotai store, and the picker must stay in the section's.
 */

/** Query-key prefix — one cache entry per distinct search. */
export const WELL_SEARCH_QUERY_KEY = 'cross-section:well-search';

export interface WellSearchRequest {
  text: string;
  page: number;
  pageSize: number;
  /**
   * Vertical holes only, as `['vertical']` — the same gate the section applies. A
   * Well to Well vertex must be a vertical hole, so a horizontal well found here would
   * be a choice the cut silently drops.
   */
  holeDirectionIds: string[];
  /**
   * Wells already on the section. Excluded server-side rather than filtered out of the
   * page after the fact: dropping rows from a page the backend already counted leaves
   * the pager describing a set that is not the one on screen.
   */
  excludeUwis: string[];
}

interface WellSearchResponse {
  results: WellsQueryResponse[];
  totalCount: number;
}

/**
 * The search's cache key.
 *
 * @param {WellSearchRequest | null} request - The search, if there is one.
 * @return {Array} A stable key.
 */
export const wellSearchQueryKey = (request: WellSearchRequest | null) => {
  if (request === null) {
    return [WELL_SEARCH_QUERY_KEY, 'idle'] as const;
  }
  return [
    WELL_SEARCH_QUERY_KEY,
    request.text,
    request.page,
    request.pageSize,
    request.holeDirectionIds,
    request.excludeUwis,
  ] as const;
};

/**
 * Runs a well search, or sits idle until there is something to search for.
 *
 * @param {WellSearchRequest | null} request - The search to run, or null.
 * @return {object} The page of wells, with its total and its state.
 */
export function useWellSearch(request: WellSearchRequest | null) {
  // An empty box is not a search for every well in the basin.
  const fetchable = request !== null && request.text.trim() !== '';

  const { data, isFetching, error } = useQuery<WellSearchResponse, Error>({
    queryKey: wellSearchQueryKey(request),
    queryFn: () =>
      api
        .post('queries/wells', { json: { query: request } })
        .json<WellSearchResponse>(),
    enabled: fetchable,
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });

  // React Query serves the previous data (`placeholderData`) without consulting
  // `enabled`, so the outputs are gated on the request rather than on the query's own
  // state.
  return {
    wells: fetchable ? (data?.results ?? []) : [],
    totalCount: fetchable ? (data?.totalCount ?? 0) : 0,
    isLoading: fetchable && isFetching && data === undefined,
    error: fetchable ? error : null,
    searched: fetchable && data !== undefined,
  };
}
